/* eslint-disable max-len */
import { useContext, useCallback, useEffect } from "react";
import DashboardIconCard from "../Content/DashboardIconCard";
import DashboardTextCard from "../Content/DashboardTextCard";
import DashboardProgressCard from "../Content/DashboardProgressCard";
import AccordionPanel from "../Content/AccordionPanel";
import StepsPagination from "../Content/StepsPagination";
import StepsHeadline from "../Content/StepsHeadline";
import { CalculationLoader } from "../Content/CalculationLoader";
import IconCircle2 from "../../Images/dashboard-cards-icons/dashboard-living-space.svg";
import IconCircle3 from "../../Images/dashboard-cards-icons/dashboard-co2-emission.svg";
import IconInfo from "../../Images/static/media/svg/info.svg";

import CalculatorContext from "../../Context/CalculatorContext";

import {
  getDashboardFuelTypeIcon,
  calculateEmission,
  getBuildingRatingChart,
  calculateTenYearsCost,
  calculateFuelConsumptionAmount,
} from "../../utils/Calculator";

function ResultsPage() {
  const {
    setCurrentStepIndex,
    forecastStartYear,
    energySource,
    fuelConsumptionAmount,
    fuelConsumptionType,
    customFernwarmeFactor,
    customEmission,
    livingSpace,
    isCommercialProperty,
    isEnergyImprovementPossible,
    isSupplyImprovementPossible,
    isSimulationEnabled,
    co2Prices,
  } = useContext(CalculatorContext);

  useEffect(() => {
    setCurrentStepIndex(9);
  }, [setCurrentStepIndex]);

  const emission = calculateEmission(
    energySource,
    fuelConsumptionType,
    fuelConsumptionAmount,
    customFernwarmeFactor,
    customEmission,
  );

  const tenYearsCost = calculateTenYearsCost(
    forecastStartYear,
    emission.defaultEmission,
    emission.totalEmission,
    +isCommercialProperty,
    +isEnergyImprovementPossible,
    +isSupplyImprovementPossible,
    livingSpace,
    co2Prices,
  );

  const buildingRatingChart = getBuildingRatingChart(
    emission.defaultEmission,
    emission.totalEmission,
    +isCommercialProperty,
    +isEnergyImprovementPossible,
    +isSupplyImprovementPossible,
    livingSpace,
  );

  const renderTenYearsCostForecastTable = useCallback(
    (forecast) => {
      if (isSimulationEnabled) {
        return forecast.map((forecastObj) => {
          return (
            <tr key={forecastObj.year}>
              <td>{forecastObj.year}</td>
              <td className="w-50">
                <div className="card card-tertiary card-progress">
                  <div className="card-progress-value">{forecastObj.landlordAmountFormatted}</div>
                  <div className="card-progress-bar bg-secondary" style={{ width: "50%" }}>
                    <div className="card-progress-value">
                      <b>- 188,39 €</b>
                    </div>
                  </div>
                </div>
              </td>
              <td className="w-50">
                <div className="card card-primary card-progress">
                  <div className="card-progress-value">{forecastObj.renterAmountFormatted}</div>
                  <div className="card-progress-bar bg-secondary" style={{ width: "50%" }}>
                    <div className="card-progress-value">
                      <b>- 188,39 €</b>
                    </div>
                  </div>
                </div>
              </td>
              <td className="text-end">
                <b>{forecastObj.totalAmount}</b>
              </td>
            </tr>
          );
        });
      }

      return forecast.map((forecastObj) => {
        return (
          <tr key={forecastObj.year}>
            <td>{forecastObj.year}</td>
            <td className="w-50">
              <div className="card card-tertiary card-progress">
                <div className="card-progress-value">{forecastObj.landlordAmountFormatted}</div>
              </div>
            </td>
            <td className="w-50">
              <div className="card card-primary card-progress">
                <div className="card-progress-value">{forecastObj.renterAmountFormatted}</div>
              </div>
            </td>
            <td className="text-end">{forecastObj.totalAmountFormatted}</td>
          </tr>
        );
      });
    },
    [isSimulationEnabled],
  );

  const renderTenYearsCostForecastBlock = useCallback(
    (forecast) => {
      if (isSimulationEnabled) {
        return forecast.map((forecastObj) => {
          return (
            <div className="block-cost-forecast-item" key={forecastObj.year}>
              <div className="block-cost-forecast-year">
                <strong>{forecastObj.year}</strong>
              </div>

              <div className="card card-tertiary card-progress">
                <div className="card-progress-name">Vermieter</div>
                <div className="card-progress-value">{forecastObj.landlordAmountFormatted}</div>
                <div className="card-progress-bar bg-secondary" style={{ width: "50%" }}>
                  <div className="card-progress-value">
                    <b>- 188,39 €</b>
                  </div>
                </div>
              </div>

              <div className="card card-primary card-progress">
                <div className="card-progress-name">Mieter</div>
                <div className="card-progress-value">{forecastObj.renterAmountFormatted}</div>
                <div className="card-progress-bar bg-secondary" style={{ width: "50%" }}>
                  <div className="card-progress-value">
                    <b>- 188,39 €</b>
                  </div>
                </div>
              </div>

              <div className="card card-transparent card-progress">
                <div className="card-progress-name">
                  <strong>Kosten gesamt</strong>
                </div>
                <div className="card-progress-value text-body">
                  <strong>{forecastObj.totalAmountFormatted}</strong>
                </div>
              </div>
            </div>
          );
        });
      }

      return forecast.map((forecastObj) => {
        return (
          <div className="block-cost-forecast-item" key={forecastObj.year}>
            <div className="block-cost-forecast-year">
              <strong>{forecastObj.year}</strong>
            </div>

            <div className="card card-tertiary card-progress">
              <div className="card-progress-name">Vermieter</div>
              <div className="card-progress-value">{forecastObj.landlordAmountFormatted}</div>
            </div>

            <div className="card card-primary card-progress">
              <div className="card-progress-name">Bewohner</div>
              <div className="card-progress-value">{forecastObj.renterAmountFormatted}</div>
            </div>

            <div className="card card-transparent card-progress">
              <div className="card-progress-name">
                <strong>Kosten gesamt</strong>
              </div>
              <div className="card-progress-value text-body">
                <strong>{forecastObj.totalAmountFormatted}</strong>
              </div>
            </div>
          </div>
        );
      });
    },
    [isSimulationEnabled],
  );

  return (
    <div className="tx-kalo form-steps-abwizard form-steps-abwizard-transparent">
      <div className="frame frame-size-default frame-full frame-background-tertiary">
        <div className="frame-group-container">
          <div className="frame-container frame-container-default">
            <CalculationLoader />
          </div>
        </div>
      </div>

      <div className="frame frame-size-default frame-indent-10 frame-type-text layout-0 frame-background-none frame-no-backgroundimage frame-pattern-none frame-space-before-none frame-space-after-extra-small">
        <div className="frame-group-container">
          <div className="frame-container frame-container-default">
            <div className="frame-inner frame-inner-space-before-extra-small frame-inner-space-after-none">
              <StepsPagination />

              <StepsHeadline>
                <h4 className="element-header text-center">
                  Prognose für die zukünftige Aufteilung Ihrer{" "}
                  <span className="text-primary">
                    CO<sub>2</sub>-Kosten
                  </span>
                </h4>
              </StepsHeadline>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 gy-3 gx-5">
                <div className="col">
                  <DashboardIconCard
                    icon={getDashboardFuelTypeIcon(energySource)}
                    preline={energySource}
                    headline={
                      +customEmission === 0
                        ? `${Intl.NumberFormat("de-DE", {
                            maximumFractionDigits: 6,
                          }).format(fuelConsumptionAmount)} ${fuelConsumptionType}`
                        : `${Intl.NumberFormat("de-DE", {
                            maximumFractionDigits: 0,
                          }).format(
                            calculateFuelConsumptionAmount(
                              energySource,
                              customEmission,
                              customFernwarmeFactor,
                            ),
                          )} kWh`
                    }
                  />
                </div>
                <div className="col">
                  <DashboardIconCard
                    icon={IconCircle2}
                    preline="Wohnfläche"
                    headline={`${livingSpace} m<sup>2</sup>`}
                  />
                </div>
                <div className="col">
                  <DashboardIconCard
                    icon={IconCircle3}
                    preline="CO<sub>2</sub>-Emission"
                    headline={`${Intl.NumberFormat("de-DE", {
                      maximumFractionDigits: 1,
                    }).format(emission.totalEmission)} t / Jahr`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="frame frame-size-default frame-indent-10 frame-type-text layout-0 frame-background-none frame-no-backgroundimage frame-pattern-none frame-space-before-none frame-space-after-extra-small">
        <div className="frame-group-container">
          <div className="frame-container frame-container-default">
            <div className="frame-inner frame-inner-space-before-none frame-inner-space-after-none">
              <div className="row">
                <div className="col-12">
                  <AccordionPanel
                    id={1}
                    isStatic
                    headlineLeft={
                      <div>
                        <div className="d-flex align-items-center">
                          <span>
                            CO<sub>2</sub>-Gesamtkosten auf 10 Jahre
                            <span className="mx-3">
                              <a
                                href="#"
                                data-bs-target="#floatinTrigerSocial"
                                tabIndex="0"
                                data-bs-toggle="modal"
                                className="accordion-button-additional-modal-trigger"
                                aria-label="Open total costs information  modal"
                              >
                                <img
                                  className="accordion-button-additional-modal-icon"
                                  src={IconInfo}
                                  alt=""
                                />
                              </a>
                            </span>
                          </span>
                        </div>
                        <div className="subline mt-3 mb-2 md-md-0">
                          {`(${forecastStartYear} - ${Number(forecastStartYear) + 9})`}
                        </div>
                      </div>
                    }
                    headlineRight={
                      <div className="accordion-button-additional-info-text">
                        {tenYearsCost.totalAmountFormatted}
                      </div>
                    }
                  >
                    <div className="row row-cols-1 row-cols-md-2 gy-3 gx-5">
                      <div className="col">
                        <DashboardTextCard
                          headlineLeft="Kosten Vermieter"
                          headlineRight={`${tenYearsCost.landlordAmountFormatted}`}
                          prelineLeft="CO<sub>2</sub>-Kostenanteil"
                          prelineRight={`${tenYearsCost.landlordPercentage} %`}
                        />
                      </div>
                      <div className="col">
                        <DashboardTextCard
                          type="primary"
                          headlineLeft="Kosten Bewohner"
                          headlineRight={`${tenYearsCost.renterAmountFormatted}`}
                          prelineLeft="CO<sub>2</sub>-Kostenanteil"
                          prelineRight={`${tenYearsCost.renterPercentage} %`}
                        />
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-12">
                        <DashboardProgressCard
                          leftValue={`${tenYearsCost.landlordPercentage}%`}
                          rightValue={`${tenYearsCost.renterPercentage}%`}
                          progressAmount={`${tenYearsCost.landlordPercentage}%`}
                        />
                      </div>
                    </div>
                  </AccordionPanel>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="frame frame-size-default frame-indent-10 frame-type-text layout-stage frame-background-none frame-has-backgroundimage frame-has-backgroundimage-svg frame-pattern-none frame-space-before-none frame-space-after-extra-small">
        <div className="frame-group-container">
          <div className="frame-container frame-container-default">
            <div className="frame-container frame-container-default">
              <div className="frame-inner decorative-banner rounded bg-tertiary frame-inner-space-before-small frame-inner-space-after-small position-relative">
                <div className="frame-backgroundimage-container">
                  <div id="frame-backgroundimage-1409" className="frame-backgroundimage">
                    <style>
                      {` 
                        #frame-backgroundimage-1409 {
                          background-image: url('https://www.kalo.de/fileadmin/user_upload/Bilder/Header/Neue_Pattern_fuer_Header/Responsiv/Mint/Muster_fuer_Header_Mint_V1-1.svg');
                        } 
                        
                        @media (min-width: 756px) {
                          #frame-backgroundimage-1409 { 
                            background-image: url('https://www.kalo.de/fileadmin/user_upload/Bilder/Header/Neue_Pattern_fuer_Header/Responsiv/Mint/Muster_fuer_Header_Mint_V1-1.svg');
                          }
                        }
                        
                        @media (min-width: 1182px) {
                          #frame-backgroundimage-1409 {
                            background-image: url('https://www.kalo.de/fileadmin/user_upload/Bilder/Header/Neue_Pattern_fuer_Header/Responsiv/Mint/Muster_fuer_Header_Mint_V1-1.svg');
                          }
                        }
                        
                        @media (min-width: 1400px) {
                          #frame-backgroundimage-1409 {
                            background-image: url('https://www.kalo.de/fileadmin/user_upload/Bilder/Header/Neue_Pattern_fuer_Header/Responsiv/Mint/Muster_fuer_Header_Mint_V1-1.svg');
                          }
                        }
                      `}
                    </style>
                  </div>
                </div>
                <div className="text-area text-white text-center px-3 px-md-5">
                  <header className="frame-header frame-header-h3 frame-header-space-small mx-5">
                    <h4 className="element-header text-center">
                      CO<sub>2</sub>-Kosten senken -{" "}
                      <span className="text-primary">auch ohne große Modernisierung.</span>
                    </h4>
                  </header>
                  <p>
                    Smarte Lösungen verbessern die Energieeffizienz Ihres Mehrparteienhauses und
                    reduzieren Ihren Kostenanteil.
                  </p>

                  <p className="text-large text-center mb-0">
                    <a
                      href="https://smart-heizen.kalo.de/vermieter-wissen/co2-kosten-senken-dank-smarter-thermostate/?etcc_cmp=Intern&etcc_med=CO2-Rechner"
                      className="btn btn-default"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Jetzt anfragen
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isCommercialProperty === "0" &&
        isEnergyImprovementPossible === "1" &&
        isSupplyImprovementPossible === "1" && (
          <div className="frame frame-size-default frame-indent-10 frame-type-text layout-0 frame-background-none frame-no-backgroundimage frame-pattern-none frame-space-before-none frame-space-after-extra-small">
            <div className="frame-group-container">
              <div className="frame-container frame-container-default">
                <div className="frame-inner frame-inner-space-before-none frame-inner-space-after-none">
                  <div className="row">
                    <div className="col-12">
                      <AccordionPanel
                        id={2}
                        headlineLeft={
                          <span>
                            CO<sub>2</sub>-Einstufung Ihres Gebäudes
                          </span>
                        }
                        headlineRight={
                          <div className="accordion-button-additional-info-legend">
                            <div className="accordion-button-additional-info-legend-item">
                              <div className="accordion-button-additional-info-legend-color bg-tertiary" />
                              <span className="accordion-button-additional-info-legend-tooltip prognose">
                                Vermieter
                              </span>
                            </div>
                            <div className="accordion-button-additional-info-legend-item">
                              <div className="accordion-button-additional-info-legend-color bg-primary" />
                              <span className="accordion-button-additional-info-legend-tooltip prognose">
                                Bewohner
                              </span>
                            </div>
                          </div>
                        }
                      >
                        <div className="overflow-auto">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: buildingRatingChart,
                            }}
                          />

                          <div
                            style={{ width: 982 }}
                            className="d-flex justify-content-between text-tertiary"
                          >
                            <span>Emissonsarme Gebäude</span>
                            <span>Emissionsreiche Gebäude</span>
                          </div>
                        </div>
                      </AccordionPanel>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="frame frame-size-default frame-indent-10 frame-type-text layout-0 frame-background-none frame-no-backgroundimage frame-pattern-none frame-space-before-none frame-space-after-medium">
        <div className="frame-group-container">
          <div className="frame-container frame-container-default">
            <div className="frame-inner frame-inner-space-before-none frame-inner-space-after-none">
              <div className="row">
                <div className="col-12">
                  <AccordionPanel
                    id={3}
                    isStatic
                    headlineLeft={
                      <div>
                        <div className="d-flex align-items-center">
                          <span>
                            CO<sub>2</sub>-Kostenprognose für 10 Jahre
                            <span className="mx-3">
                              <a
                                href="#"
                                data-bs-target="#floatinTrigerContact"
                                tabIndex="0"
                                data-bs-toggle="modal"
                                className="accordion-button-additional-modal-trigger"
                                aria-label="Open cost forecast information modal"
                              >
                                <img
                                  className="accordion-button-additional-modal-icon"
                                  src={IconInfo}
                                  alt=""
                                />
                              </a>
                            </span>
                          </span>
                        </div>
                        <div className="subline mt-3 mb-2 mb-md-0">
                          {`(${forecastStartYear} - ${Number(forecastStartYear) + 9})`}
                        </div>
                      </div>
                    }
                    headlineRight={
                      <div className="accordion-button-additional-info-legend">
                        <div className="accordion-button-additional-info-legend-item">
                          <div className="accordion-button-additional-info-legend-color bg-tertiary" />
                          <span className="accordion-button-additional-info-legend-tooltip prognose">
                            Vermieter
                          </span>
                        </div>
                        <div className="accordion-button-additional-info-legend-item">
                          <div className="accordion-button-additional-info-legend-color bg-primary" />
                          <span className="accordion-button-additional-info-legend-tooltip prognose">
                            Bewohner
                          </span>
                        </div>
                      </div>
                    }
                  >
                    <div className="overflow-auto d-none d-lg-block">
                      <div className="table-responsive">
                        <table className="table table-striped table-cost-forecast">
                          <thead>
                            <tr>
                              <th>Jahr</th>
                              <th>Vermieter</th>
                              <th>Bewohner</th>
                              <th>Kosten gesamt</th>
                            </tr>
                          </thead>
                          <tbody>{renderTenYearsCostForecastTable(tenYearsCost.forecast)}</tbody>
                        </table>
                      </div>
                    </div>

                    <div className="d-block d-lg-none">
                      <div className="block-cost-forecast">
                        {renderTenYearsCostForecastBlock(tenYearsCost.forecast)}
                      </div>
                    </div>
                  </AccordionPanel>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="frame frame-size-default frame-indent-8 frame-type-text layout-0 frame-background-none frame-no-backgroundimage frame-pattern-none frame-space-before-none frame-space-after-medium">
        <div className="frame-group-container">
          <div className="frame-group-inner">
            <div className="frame-container frame-container-default">
              <div className="frame-inner frame-inner-space-before-none frame-inner-space-after-none">
                <header className="frame-header frame-header-h3 frame-header-space-default">
                  <h3 className="element-header text-center">
                    {/* <span>
                      <span className="text-primary">
                        Ihr CO<sub>2</sub>–Kostenanteil
                      </span>{" "}
                      liegt voraussichtlich bei {tenYearsCost.landlordPercentage} %
                    </span> */}
                    Sie wollen Ihre CO<sub>2</sub>-Kosten{" "}
                    <span className="text-primary">dauerhaft reduzieren?</span>
                  </h3>
                </header>
                <p className="text-large text-center">
                  Mit unserem <strong>datenbasierten Heizungsmonitoring (Heat Sense)</strong> und
                  den <strong>smarten Thermostaten (Heat Control)</strong> reduzieren Sie den
                  Heizenergieverbrauch Ihrer Liegenschaft spürbar, steigern die Energieeffizienz und
                  senken Ihre CO<sub>2</sub>-Gesamtkosten nachhaltig – ganz ohne große
                  Modernisierungsmaßnahmen.
                </p>
                <p className="text-large text-center">
                  <strong>Heat Sense</strong> analysiert kontinuierlich die Betriebsdaten im
                  Heizungskeller und optimiert die Wärmeerzeugung automatisch.{" "}
                  <strong>Heat Control</strong> übernimmt den hydraulischen Abgleich dauerhaft
                  direkt am Heizkörper – minimalinvasiv, wartungsarm und mit präzisen
                  Verbrauchsdaten aus jeder Wohnung.
                </p>
                <p className="text-large text-center">
                  Das Ergebnis:{" "}
                  <strong>
                    volle Transparenz, messbar weniger Verbrauch, dauerhaft niedrigere Kosten
                  </strong>{" "}
                  und ein <strong>optimal eingestelltes Heizsystem</strong>, das gesetzlichen
                  Anforderungen entspricht – <strong>ohne Eingriffe in die Gebäudestruktur.</strong>
                </p>

                <p className="text-large text-center">
                  <a
                    href="https://smart-heizen.kalo.de/vermieter-wissen/co2-kosten-senken-dank-smarter-thermostate/?etcc_cmp=Intern&etcc_med=CO2-Rechner"
                    className="btn btn-default"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Mehr erfahren
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;
